import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  ContainerView,
  TitlePage,
} from "../../components/Container";
import { useApp } from "../../contexts";
import { IconOpenEye } from "../../components/Icons/IconOpenEye";
import { IconCloseEye } from "../../components/Icons/IconCloseEye";
import {
  ssoUpdatePassword,
  ssoGetUserInfo,
  ssoSendOtpUpdatePassword,
  ssoCheckPassword,
  ssoIsNewPasswordFlagOn,
} from "../../services/app";
import * as Notification from "../../Notification";
import { encryptClientPassword } from "../../utils/helper";
import { IconValidPassword } from "../../components/Icons/IconValidPassword";
import { IconInvalidPassword } from "../../components/Icons/IconInvalidPassword";
import { ChangePasswordConfirm } from "./ModalOtp";
import { useRef } from "react";
import { ChangePasswordLegacy } from "./legacy";

const OTP_COOL_DOWN = 180; // 3 minutes

const ERROR_DEFAULT = {
  password: "",
  new_password: "",
  confirm_password: "",
  twofa: "",
  otp: "",
  form: "",
};

const PasswordInput = ({
  id = "",
  label = "",
  placeholder = "",
  defaultValue = "",
  onInput = () => {},
  errorText = "",
  maxLength = 20,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <>
      <div className="mb-6 lg:mb-[25px]">
        <label
          htmlFor={id}
          className="block text-suzu-gray-9 w-full mb-1 text-[15px] lg:text-base"
        >
          {label}
          <span className="ml-[1px] text-red-600">*</span>
        </label>
        <div className={`w-full lg:w-[650px] p-[1px] rounded-[10px] relative`}>
          <input
            id={id}
            type={showPassword ? "text" : "password"}
            className={`h-[52px] w-full lg:max-w-full lg:w-[650px] px-5 py-3
            bg-white rounded-[10px] text-black input-gradient`}
            placeholder={placeholder}
            defaultValue={defaultValue}
            onInput={onInput}
            maxLength={maxLength}
          />
          <div
            className={`w-6 h-6 flex justify-center ${
              showPassword ? "items-center" : "items-end"
            } absolute right-4 top-3.5 z-10 cursor-pointer`}
            onClick={() => toggleShowPassword()}
          >
            {showPassword && <IconOpenEye />}
            {!showPassword && <IconCloseEye />}
          </div>
        </div>
        {errorText && <p className="text-red-600 text-sm">{errorText}</p>}
      </div>
    </>
  );
};

const PasswordCriteriaLine = ({ id, label, isPassed }) => {
  return (
    <>
      <p id={id} className="pwd-criteria">
        {isPassed ? (
          <>
            <IconValidPassword />
          </>
        ) : (
          <>
            <IconInvalidPassword />
          </>
        )}
        {label}
      </p>
    </>
  );
};

const PasswordCriteriaSection = ({ criteriaEvaluation }) => {
  const { T } = useApp();
  return (
    <>
      <div className="flex w-full flex-col gap-1 mb-5">
        <p className="pwd-criteria-title">{T("Must contain:")}</p>
        <PasswordCriteriaLine
          id="pwd_criteria1"
          label={T("From 8 to 20 characters")}
          isPassed={criteriaEvaluation?.lengthCriteria}
        />
        <PasswordCriteriaLine
          id="pwd_criteria2"
          label={T("At least 1 letter (A-Z or a-z)")}
          isPassed={criteriaEvaluation?.letterCriteria}
        />
        <PasswordCriteriaLine
          id="pwd_criteria3"
          label={T("At least 1 number (0-9)")}
          isPassed={criteriaEvaluation?.numberCriteria}
        />
        <PasswordCriteriaLine
          id="pwd_criteria4"
          label={
            <span>
              <span>
                {T("At least 1 special character in the list")}
              </span>
              <br/>
              <span style={{ letterSpacing: "-1.5px" }}>
                {("! @ # $ % ^ & * ( ) - _ + = { } [ ] \\ | / : ; \" ' < > ? ~ ` .  ,")}
              </span>
            </span>
          }
          isPassed={criteriaEvaluation?.specialCharCriteria}
        />
      </div>
    </>
  );
};

const PasswordStrengthSection = ({ passwordStrength }) => {
  const { T } = useApp();
  const LVLs = {
    WEAK: "weak",
    MEDIUM: "medium",
    STRONG: "strong",
  };
  const getStrengthClass = (strength) => {
    switch (strength) {
      case 1:
      case 2:
        return LVLs.WEAK;
      case 3:
        return LVLs.MEDIUM;
      case 4:
        return LVLs.STRONG;
      default:
        return "";
    }
  };
  const groupClass = getStrengthClass(passwordStrength);
  const barClass1 = passwordStrength >= 1 ? "active" : "";
  const barClass2 = passwordStrength >= 3 ? "active" : "";
  const barClass3 = passwordStrength >= 4 ? "active" : "";

  const strengthLabel = () => {
    switch (passwordStrength) {
      case 1:
      case 2:
        return T("Weak password");
      case 3:
        return T("Moderate password");
      case 4:
        return T("Strong password");
      default:
        return "";
    }
  };

  return (
    <>
      <div className="max-w-[648px]">
        <div id="pwd_strength_group" className={`pwd-strength-group ${groupClass}`}>
          <div id="pwd_strength1" className={`pwd-strength ${barClass1}`}></div>
          <div id="pwd_strength2" className={`pwd-strength ${barClass2}`}></div>
          <div id="pwd_strength3" className={`pwd-strength ${barClass3}`}></div>
        </div>
        <div id="pwd_strength_label" className={`pwd-strength-label ${groupClass}`}>
          {strengthLabel()}
        </div>
      </div>
    </>
  );
};

export const ChangePassword = () => {
  const { T, user } = useApp();

  const [formData, $formData] = useState({
    password: "",
    new_password: "",
    confirm_password: "",
    otp: "",
  });

  const formRef = useRef(null);
  const [applyNewPassword, setApplyNewPassword] = useState(false);
  
  const [userData, $userData] = useState(null);
  const [isSettedPass, $isSettedPass] = useState(true);
  const [error, $error] = useState(ERROR_DEFAULT);
  const [submitting, setSubmitting] = useState(false);

  const [allowSendCode, setAllowSendCode] = useState(true);
  const [sendingCode, setSendingCode] = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(0);
  const [showOtpModal, setShowOtpModal] = useState(false);

  const startCooldown = () => {
    setAllowSendCode(false);
    setOtpCooldown(OTP_COOL_DOWN);
    const interval = setInterval(() => {
      setOtpCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setAllowSendCode(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const reloadNewPasswordFlag = async () => {
    const isOn = await ssoIsNewPasswordFlagOn();
    setApplyNewPassword(isOn);
  }

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!canOpenOtpModal()) {
      return;
    }
    if (sendingCode || !allowSendCode) {
      return;
    }

    const isBeValidated = await checkOldPassword();
    if (!isBeValidated) {
      return;
    }

    try {
      setSendingCode(true);
      const res = await ssoSendOtpUpdatePassword();
      if (!res?.data?.status) {
        throw new Error(res?.data?.message);
      }

      startCooldown();
      setShowOtpModal(true);
      Notification.success(T("Verification Code Sent!"));
    } catch (error) {
      console.error("Error sending OTP:", error);
      const errMsg = error?.message;
      if (errMsg === "maximum_request_otp") {
        Notification.error(T("You have sent too many requests. Please try again later."));
      }
    } finally {
      setSendingCode(false);
    }
  }

  const handleResendCode = async () => {
    if (sendingCode || !allowSendCode) {
      return;
    }
    try {
      setSendingCode(true);
      await ssoSendOtpUpdatePassword();
    } catch (error) {
      console.error("Error sending OTP:", error);
    } finally {
      setSendingCode(false);
      startCooldown();
      setShowOtpModal(true);
      Notification.success(T("Verification Code Sent!"));
    }
  }

  const validateOldPassword = (input) => {
    if (!isSettedPass) {
      return true;
    }
    const isValid = input && input.length > 0;
    return isValid;
  };

  const getNewPasswordError = (input) => {
    const validLength = input?.length >= 8 && input?.length <= 20;
    const validLetter = /[a-zA-Z]/.test(input);
    const validNumber = /\d/.test(input);
    // eslint-disable-next-line no-useless-escape
    const validSpecialChar = /[!@#$%^&*()\-_+={}\[\]\\|\/:;.,"'<>?~`]/g.test(
      input
    );
    // eslint-disable-next-line no-useless-escape
    const containsOnlyAllowedChar = /^[A-Za-z\d!@#$%^&*()\-_+={}\[\]\\|\/:;.",'<>?~`]+$/.test(input);
    const isSameAsOldPassword = input === formData.password;

    const meetDisplayCriteria =
      validLength &&
      validLetter &&
      validNumber &&
      validSpecialChar;

    if (!meetDisplayCriteria) {
      return T("password_invalid");
    }

    if (isSameAsOldPassword) {
      return T("Cannot be the same as your previous password");
    }

    if (!containsOnlyAllowedChar) {
      return T("Your password contains invalid characters.");
    }

    return "";
  };
    

  const validateNewPassword = (input) => {
    const errorMsg = getNewPasswordError(input);
    return !errorMsg;
  };

  const validateConfirmPassword = (input) => {
    const isValid = input === formData?.new_password;
    return isValid;
  };

  const newPwdEvaluation = useMemo(() => {
    const passwordInput = formData?.new_password;

    const lengthCriteria =
      passwordInput?.length >= 8 && passwordInput?.length <= 20;
    const letterCriteria = /[a-zA-Z]/.test(passwordInput);
    const numberCriteria = /\d/.test(passwordInput);
    // eslint-disable-next-line no-useless-escape
    const specialCharCriteria = /[!@#$%^&*()\-_+={}\[\]\\|\/:;.,"'<>?~`]/g.test(
      passwordInput
    );
    // eslint-disable-next-line no-useless-escape
    const containsOnlyAllowedChar = /^[A-Za-z\d!@#$%^&*()\-_+={}\[\]\\|\/:;.",'<>?~`]+$/.test(passwordInput);

    const result = {
      lengthCriteria,
      letterCriteria,
      numberCriteria,
      specialCharCriteria,
      containsOnlyAllowedChar,
    };
    return result;
  }, [formData.new_password]);

  const passwordStrength = useMemo(() => {
    let strength = 0;
    if (newPwdEvaluation.lengthCriteria) strength++;
    if (newPwdEvaluation.letterCriteria) strength++;
    if (newPwdEvaluation.numberCriteria) strength++;
    if (newPwdEvaluation.specialCharCriteria) strength++;
    return strength;
  }, [newPwdEvaluation]);

  const handleInput = (key, val) => {
    $formData((data) => {
      return {
        ...data,
        [key]: val,
      };
    });
  };

  const handleInputOldPassword = (e) => {
    const value = e.target.value;
    const isValid = validateOldPassword(value);
    const isSameAsNewPassword = value === formData?.new_password;
    if (!isValid) {
      $error({ ...error, password: T("password_required") });
    } else {
      $error({ ...error, password: "" });
    }
    if (isSameAsNewPassword) {
      $error({ ...error, new_password: T("Cannot be the same as your previous password") })
    } else {
      const errorMsg = getNewPasswordError(value);
      $error({ ...error, new_password: errorMsg })
    }
    handleInput("password", value);
  };

  const handleInputNewPassword = (e) => {
    const value = e.target.value;
    
    const errorMsg = getNewPasswordError(value);
    $error({ ...error, new_password: errorMsg });

    handleInput("new_password", value);
  };

  const handleInputConfirmPassword = (e) => {
    const value = e.target.value;
    const isValid = validateConfirmPassword(value);
    if (!isValid) {
      $error({ ...error, confirm_password: T("confirm_password_invalid") });
    } else {
      $error({ ...error, confirm_password: "" });
    }
    handleInput("confirm_password", value);
  };

  const isFormValid = () => {
    const isNewPasswordValid = validateNewPassword(formData.new_password);
    const isConfirmPasswordValid = validateConfirmPassword(
      formData.confirm_password
    );
    const isOldPasswordValid = validateOldPassword(formData.password);

    const isValid =
      isNewPasswordValid && isConfirmPasswordValid && isOldPasswordValid;
    return isValid;
  };

  const canOpenOtpModal = () => {
    return isFormValid();
  }

  const loadProfile = () => {
    ssoGetUserInfo().then((res) => {
      const data = res.data?.data || {};
      $userData(data);
      if (!data.has_password) {
        $isSettedPass(false);
      } else {
        $isSettedPass(true);
      }
    });
  };

  const checkOldPassword = async () => {
    try {
      const res = await ssoCheckPassword({
        old_password: encryptClientPassword(formData.password),
        new_password: encryptClientPassword(formData.new_password),
      })
      if (!res?.data?.status) {
        const msg = T("The current password is incorrect.");
        Notification.error(T(msg));
        $error({ ...error, password: msg });
        return false;
      }
    } catch (err) {
      console.error("AccountService.checkOldPassword=>ERROR", err);
      const msg = err?.response?.data?.message;
      Notification.error(T(msg));
      return false;
    }
    return true;
  };

  const changePassword = (otp) => {
    ssoUpdatePassword({
      old_password: isSettedPass
        ? encryptClientPassword(formData.password)
        : undefined,
      new_password: encryptClientPassword(formData.new_password),
      otp,
    })
      .then((res) => {
        if (res?.data?.status) {
          $formData({
            password: "",
            new_password: "",
            confirm_password: "",
            otp: "",
          });
          $error(ERROR_DEFAULT);
          if (!isSettedPass) {
            $isSettedPass(true);
          }
          formRef?.current?.reset();
          setShowOtpModal(false);
          setOtpCooldown(0);
          Notification.success(T("Change password success."));
          loadProfile();
        } else {
          if (+res?.data?.code === 102) {
            $error({
              ...ERROR_DEFAULT,
              password: T("The current password is incorrect."),
            });
          } else if (+res?.data?.code === 103) {
            $error({
              ...ERROR_DEFAULT,
              form: T("Cannot be the same as your previous password"),
            });
          } else if (+res?.data?.code === 104) {
            $error({ ...ERROR_DEFAULT, otp: T("The verification code is incorrect.") });
          } else if (+res?.data?.code === 5000) {
            $error({ ...ERROR_DEFAULT, otp: T("The verification code is incorrect.") });
          } else if (res?.data?.message === "otp_invalid") {
            $error({ ...ERROR_DEFAULT, otp: T("The verification code is incorrect.") });
          } else {
            $error({ ...ERROR_DEFAULT, form: T("Change password fail.") });
            Notification.error(T('Change password fail.'));
          }
        }
      })
      .catch((err) => {
        console.log("AccountService.changePassword=>ERROR", err);
        Notification.error(T("Change password fail."));
      });
  };

  const handleCloseOtpModal = () => {
    setShowOtpModal(false);
    setAllowSendCode(true);
    setOtpCooldown(0);
    setSendingCode(false);
    $error(ERROR_DEFAULT);
  };

  const submitFormChangePassword = (otp) => {
    const isValid = isFormValid();
    if (!isValid) {
      return;
    }

    try {
      setSubmitting(true);
      $error(ERROR_DEFAULT);

      handleInput("otp", otp);
      changePassword(otp);
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    loadProfile();
    reloadNewPasswordFlag();
  }, []);

  const renderContent = () => {
    return <>
    {userData && (
      <Container>
        <ContainerView background="transparent">
          <TitlePage>{T("Password")}</TitlePage>
          <div className="flex justify-between items-start w-full  container mt-[32px] lg:gap-12">
            <div className="w-full block grow-1">
              <form
                className="w-full"
                onSubmit={handleSendOtp}
                id="form_change_password"
                ref={formRef}
              >
                {isSettedPass && (
                  <PasswordInput
                    id="form_old_password"
                    label={T("Current Password")}
                    placeholder={T("Enter current password")}
                    defaultValue={formData?.password || ""}
                    onInput={handleInputOldPassword}
                    errorText={error.password}
                  />
                )}
                <PasswordInput
                  id="form_new_password"
                  label={T("New Password")}
                  placeholder={T("Enter new password")}
                  defaultValue={formData?.new_password || ""}
                  onInput={handleInputNewPassword}
                  errorText={error.new_password}
                />
                <PasswordInput
                  id="form_confirm_password"
                  label={T("Confirm New Password")}
                  placeholder={T("Enter new password again")}
                  defaultValue={formData?.confirm_password || ""}
                  onInput={handleInputConfirmPassword}
                  errorText={error.confirm_password}
                />

                <PasswordStrengthSection
                  passwordStrength={passwordStrength}
                />
                <PasswordCriteriaSection
                  criteriaEvaluation={newPwdEvaluation}
                />

                <div>
                  {error.form && (
                    <p className="mb-2 text-suzu-error text-sm">
                      {error.form}
                    </p>
                  )}
                </div>

                <div className="pt-3 pb-[100px]">
                  <button
                    className="bg-black w-[344px] max-w-full block mx-auto 
                    lg:mx-0 lg:w-[190px] h-[52px] text-center text-white rounded-[15px]
                    disabled:cursor-not-allowed disabled:opacity-70"
                    type="submit"
                    disabled={!canOpenOtpModal()}
                  >
                    {T("Save")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </ContainerView>
      </Container>
    )}
    {showOtpModal && 
      <ChangePasswordConfirm
        onClose={handleCloseOtpModal}
        email={user?.email}
        otpCooldown={otpCooldown}
        allowSendCode={allowSendCode}
        onResend={handleResendCode}
        onSubmit={submitFormChangePassword}
        isLoading={submitting}
        errorText={error.otp}
      />
    }
  </>
  }

  const renderContentLegacy = () => {
    return <ChangePasswordLegacy/>
  }

  return (
    applyNewPassword ? renderContent() : renderContentLegacy()
  );
};
